import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Search, RotateCcw, Filter, ChevronRight, X, Navigation } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWdlbmNlbXNjIiwiYSI6ImNtbmN6NGN6ajEyNjYycnNoMnhoZnB2cm4ifQ.DR23BWxFiKaOpi3fHH06mQ';

const LENS_TYPES = [
  'Lentille sclérale', 'Kératocône', 'Orthokératologie', 'Lentille rigide', 'Lentille Souple'
];

const BannerCarousel = ({ banners }) => {
  const [current, setCurrent] = useState(0);
  const baseUrl = API.replace('/api', '');

  useEffect(() => {
    if (banners.length <= 1) return;
    const timer = setInterval(() => setCurrent(prev => (prev + 1) % banners.length), 6000);
    return () => clearInterval(timer);
  }, [banners.length]);

  if (!banners.length) return null;
  const b = banners[current];
  const imgSrc = b.image_url?.startsWith('/') ? `${baseUrl}${b.image_url}` : b.image_url;

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-sm max-w-2xl mx-auto" data-testid="banner-carousel">
      <a href={b.link_url || '#'} target="_blank" rel="noopener noreferrer"
        onClick={() => { if (b.id) axios.post(`${API}/ad-banners/${b.id}/click`).catch(() => {}); }}
        className="block">
        <img src={imgSrc} alt={b.title || ''} className="w-full h-auto" style={{ aspectRatio: '4 / 1', objectFit: 'cover' }} />
      </a>
      {banners.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {banners.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-white w-5' : 'bg-white/50'}`} />
          ))}
        </div>
      )}
    </div>
  );
};

const DirectoryPage = () => {
  const navigate = useNavigate();
  const [practitioners, setPractitioners] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [selectedLens, setSelectedLens] = useState([]);
  const [searchLocation, setSearchLocation] = useState('');
  const [sortOrder, setSortOrder] = useState('a-z');
  const [banners, setBanners] = useState([]);
  const [topBanners, setTopBanners] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [userLocation, setUserLocation] = useState(null); // [lng, lat]
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);
  const popupRef = useRef(null);
  const filteredRef = useRef([]);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { filterData(); }, [searchName, selectedLens, searchLocation, sortOrder, practitioners, userLocation]);

  useEffect(() => {
    if (!loading && mapRef.current && !mapInstance.current) initMap();
  }, [loading]);

  useEffect(() => {
    filteredRef.current = filtered;
    if (mapInstance.current) updateMarkers();
  }, [filtered]);

  useEffect(() => {
    [...banners, ...topBanners].forEach(b => {
      if (b.id) axios.post(`${API}/ad-banners/${b.id}/view`).catch(() => {});
    });
  }, [banners, topBanners]);

  const fetchData = async () => {
    try {
      const [pRes, bSidebar, bTop] = await Promise.all([
        axios.get(`${API}/practitioners`),
        axios.get(`${API}/ad-banners?placement=directory_sidebar&status=active`).catch(() => ({ data: [] })),
        axios.get(`${API}/ad-banners?placement=directory_top&status=active`).catch(() => ({ data: [] })),
      ]);
      setPractitioners(pRes.data);
      setBanners(bSidebar.data);
      setTopBanners(bTop.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Haversine distance between two [lng, lat] in km
  const haversineKm = (a, b) => {
    if (!a || !b) return null;
    const [lng1, lat1] = a, [lng2, lat2] = b;
    const R = 6371;
    const toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };

  const filterData = () => {
    let data = [...practitioners];
    if (searchName) data = data.filter(d => d.name.toLowerCase().includes(searchName.toLowerCase()));
    if (selectedLens.length > 0) data = data.filter(d => selectedLens.some(l => (d.specialties || []).some(s => s.toLowerCase().includes(l.toLowerCase()))));
    if (searchLocation) data = data.filter(d => (d.address || '').toLowerCase().includes(searchLocation.toLowerCase()) || (d.city || '').toLowerCase().includes(searchLocation.toLowerCase()));
    // Compute distance from user location (km), null if unknown
    if (userLocation) {
      data = data.map(d => ({
        ...d,
        distance_km: (d.longitude && d.latitude) ? haversineKm(userLocation, [d.longitude, d.latitude]) : null,
      }));
    }
    if (sortOrder === 'a-z') data.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortOrder === 'z-a') data.sort((a, b) => b.name.localeCompare(a.name));
    else if (sortOrder === 'distance') {
      data.sort((a, b) => {
        const da = a.distance_km == null ? Infinity : a.distance_km;
        const db = b.distance_km == null ? Infinity : b.distance_km;
        return da - db;
      });
    }
    setFiltered(data);
  };

  const toggleLens = (lens) => {
    setSelectedLens(prev => prev.includes(lens) ? prev.filter(l => l !== lens) : [...prev, lens]);
  };

  const resetFilters = () => {
    setSearchName(''); setSelectedLens([]); setSearchLocation(''); setSortOrder('a-z');
  };

  const initMap = async () => {
    try {
      const mapboxgl = (await import('mapbox-gl')).default;
      mapboxgl.accessToken = MAPBOX_TOKEN;

      const map = new mapboxgl.Map({
        container: mapRef.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [2.3, 46.8],
        zoom: 5.2,
        language: 'fr',
      });

      map.addControl(new mapboxgl.NavigationControl(), 'top-left');

      // GeolocateControl: tap to center the map on the visitor's location
      const geolocateCtrl = new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
        showUserHeading: true,
        showAccuracyCircle: true,
      });
      geolocateCtrl.on('geolocate', (e) => {
        const { longitude, latitude } = e.coords || {};
        if (typeof longitude === 'number' && typeof latitude === 'number') {
          setUserLocation([longitude, latitude]);
          setSortOrder('distance');
        }
      });
      map.addControl(geolocateCtrl, 'top-left');

      map.on('load', () => {
        // Set French labels
        const layers = map.getStyle().layers;
        layers.forEach(layer => {
          if (layer.layout && layer.layout['text-field']) {
            map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', 'name_fr'], ['get', 'name']]);
          }
        });
        mapInstance.current = map;
        updateMarkers();
      });
    } catch (e) { console.error('Map init error:', e); }
  };

  const updateMarkers = async () => {
    if (!mapInstance.current) return;
    const mapboxgl = (await import('mapbox-gl')).default;
    const data = filteredRef.current;

    // Clear existing markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    const DEFAULT_MARKER_ICON = '/avatar-SFOALC.png';

    data.forEach(d => {
      if (!d.latitude || !d.longitude) return;

      const el = document.createElement('div');
      el.className = 'mapbox-marker';
      el.style.cssText = 'width:42px;height:42px;cursor:pointer';

      const inner = document.createElement('div');
      inner.className = 'marker-inner';
      inner.style.cssText = 'width:100%;height:100%;border-radius:50%;border:2.5px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.25);overflow:hidden;transition:transform .3s,box-shadow .3s;transform:scale(1);background:#fff;display:flex;align-items:center;justify-content:center';

      const img = document.createElement('img');
      const rawAvatar = d.profile_image;
      if (rawAvatar) {
        img.src = rawAvatar.startsWith('http') ? rawAvatar : `${BACKEND_URL}${rawAvatar}`;
      } else {
        img.src = DEFAULT_MARKER_ICON;
      }
      img.style.cssText = 'width:100%;height:100%;object-fit:cover';
      img.onerror = () => {
        img.src = DEFAULT_MARKER_ICON;
        img.style.cssText = 'width:100%;height:100%;object-fit:cover';
      };
      inner.appendChild(img);
      el.appendChild(inner);

      el.addEventListener('mouseenter', () => {
        inner.style.transform = 'scale(1.3)';
        inner.style.boxShadow = '0 0 0 4px rgba(58,143,183,0.3), 0 4px 16px rgba(0,0,0,.3)';
        el.style.zIndex = '10';
        setHoveredId(d.id);
      });
      el.addEventListener('mouseleave', () => {
        inner.style.transform = 'scale(1)';
        inner.style.boxShadow = '0 2px 8px rgba(0,0,0,.25)';
        el.style.zIndex = '1';
        setHoveredId(null);
      });

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (popupRef.current) popupRef.current.remove();
        
        // Lower all marker z-indexes so popup stays on top
        markersRef.current.forEach(m => { const mel = m.getElement(); if (mel) mel.style.zIndex = '1'; });
        
        const popupContent = document.createElement('div');
        popupContent.style.cssText = "font-family:'Roboto',sans-serif;padding:4px";
        popupContent.innerHTML = `
          <p style="font-weight:700;font-size:14px;color:#1e3a5f;margin:0 0 4px">${d.name || d.user_name || ''}</p>
          <p style="font-size:12px;color:#666;margin:0 0 2px">${d.address || ''}, ${d.city || ''}</p>
          <p style="font-size:12px;color:#35AFE4;margin:0 0 2px">${d.phone || d.consultation_phone || ''}</p>
          ${d.parking_info ? `<p style="font-size:11px;color:#888;margin:0 0 4px">🅿 ${d.parking_info}${d.parking_address ? ` <a href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.parking_address)}" target="_blank" style="color:#35AFE4;text-decoration:underline">Itinéraire</a>` : ''}</p>` : ''}
        `;
        const btn = document.createElement('button');
        btn.textContent = 'Voir la fiche';
        btn.style.cssText = 'display:inline-block;padding:5px 16px;background:#35AFE4;color:#fff;border-radius:20px;font-size:11px;font-weight:600;border:none;cursor:pointer';
        btn.addEventListener('click', () => navigate(`/annuaire/${d.id}`));
        popupContent.appendChild(btn);
        
        const popup = new mapboxgl.Popup({ offset: 25, closeButton: true, maxWidth: '260px', className: 'sfoalc-popup' })
          .setLngLat([d.longitude, d.latitude])
          .setDOMContent(popupContent)
          .addTo(mapInstance.current);
        popupRef.current = popup;
      });

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([d.longitude, d.latitude])
        .addTo(mapInstance.current);

      marker._id = d.id;
      markersRef.current.push(marker);
    });
  };

  // Sync hover from list to map — only highlight, no flyTo
  useEffect(() => {
    markersRef.current.forEach(m => {
      const el = m.getElement();
      if (!el) return;
      const inner = el.querySelector('.marker-inner');
      if (!inner) return;
      const active = m._id === hoveredId;
      inner.style.transform = active ? 'scale(1.3)' : 'scale(1)';
      inner.style.boxShadow = active ? '0 0 0 4px rgba(58,143,183,0.3), 0 4px 16px rgba(0,0,0,.3)' : '0 2px 8px rgba(0,0,0,.25)';
      el.style.zIndex = active ? '10' : '1';
    });
  }, [hoveredId]);

  return (
    <div className="min-h-screen bg-[#f0f8fb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="directory-page">
      <SEO title="Annuaire des ophtalmologistes" description="Trouvez un ophtalmologiste adaptateur de lentilles de contact près de chez vous. Annuaire SFOALC." />
      <SiteHeader />
      <link href="https://api.mapbox.com/mapbox-gl-js/v3.20.0/mapbox-gl.css" rel="stylesheet" />
      <style>{`
        .mapboxgl-popup { z-index: 100 !important; }
        .mapboxgl-popup-content { border-radius: 12px !important; padding: 14px !important; box-shadow: 0 8px 30px rgba(0,0,0,0.18) !important; }
        .mapboxgl-popup-close-button { font-size: 18px; padding: 4px 8px; color: #666; }
        .mapboxgl-popup-tip { border-top-color: white !important; }
        .mapbox-marker { z-index: 1; }
      `}</style>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #35AFE4 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 py-14 sm:py-20 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="directory-title">
            L'annuaire des ophtalmologistes
          </h1>
          <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto">
            Trouvez un ophtalmologiste adaptateur de lentilles de contact près de chez vous
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '40px' }}>
            <path fill="#f0f8fb" d="M0,60V50C0.9,33,126.7,0,500,0S999.1,33,1000,50V60H0Z" />
          </svg>
        </div>
      </section>

      {/* Top Banner — removed, banners now on Formations page */}

      {/* Split layout */}
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-4 lg:h-[calc(100vh-200px)] lg:min-h-[600px]" data-testid="split-layout">

          {/* Map: top on mobile (320px), right on desktop (1/2 width) */}
          <div className="order-first lg:order-last w-full lg:w-1/2 h-[320px] lg:h-auto rounded-2xl overflow-hidden border border-[#d0e8f0]" data-testid="map-panel">
            <div ref={mapRef} style={{ width: '100%', height: '100%' }} data-testid="directory-map" />
          </div>

          {/* LEFT: Filters + Cards */}
          <div className="w-full lg:w-1/2 flex flex-col overflow-hidden lg:h-full">
            {/* Filters panel */}
            <div className="bg-white rounded-t-2xl border border-[#d0e8f0] p-4 flex-shrink-0" data-testid="filters-panel">
              <div className="flex items-center gap-2 mb-3">
                <Filter size={14} className="text-[#35AFE4]" />
                <h3 className="text-sm font-bold text-[#35AFE4]" style={{ fontFamily: "'Barlow', sans-serif" }}>Filtres</h3>
                <span className="ml-auto text-xs text-gray-400 font-medium" data-testid="results-count">
                  <strong className="text-[#1e3a5f]">{filtered.length}</strong> résultats
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Quel praticien recherchez-vous ?</label>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input value={searchName} onChange={e => setSearchName(e.target.value)}
                      placeholder="Nom du praticien"
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#35AFE4]"
                      data-testid="search-name"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Pour quel type de lentille ?</label>
                  <div className="flex flex-wrap gap-1.5">
                    {LENS_TYPES.map(lens => (
                      <button key={lens} onClick={() => toggleLens(lens)}
                        className={`text-[11px] px-2.5 py-1 rounded-full font-semibold transition-all ${selectedLens.includes(lens) ? 'bg-[#35AFE4] text-white' : 'bg-[#eaf6fc] text-[#35AFE4] hover:bg-[#d5ebf2]'}`}
                        data-testid={`filter-lens-${lens}`}
                      >
                        {lens}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 block">Localisation</label>
                  <input value={searchLocation} onChange={e => setSearchLocation(e.target.value)}
                    placeholder="Ville ou adresse"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#35AFE4]"
                    data-testid="search-location"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex gap-1 flex-wrap">
                    <button onClick={() => setSortOrder('a-z')} className={`text-[10px] px-2 py-1 rounded font-bold ${sortOrder === 'a-z' ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-500'}`} data-testid="sort-az">A-Z</button>
                    <button onClick={() => setSortOrder('z-a')} className={`text-[10px] px-2 py-1 rounded font-bold ${sortOrder === 'z-a' ? 'bg-[#1e3a5f] text-white' : 'bg-gray-100 text-gray-500'}`} data-testid="sort-za">Z-A</button>
                    {userLocation && (
                      <button onClick={() => setSortOrder('distance')} className={`text-[10px] px-2 py-1 rounded font-bold flex items-center gap-1 ${sortOrder === 'distance' ? 'bg-[#35AFE4] text-white' : 'bg-[#eaf6fc] text-[#35AFE4]'}`} data-testid="sort-distance">
                        <MapPin size={10} /> Proximité
                      </button>
                    )}
                  </div>
                  <button onClick={resetFilters} className="text-[11px] text-gray-400 hover:text-[#35AFE4] font-medium flex items-center gap-1" data-testid="reset-filters">
                    <RotateCcw size={11} /> Réinitialiser
                  </button>
                </div>
              </div>
            </div>

            {/* Cards list - scrollable */}
            <div className="flex-1 overflow-y-auto border-x border-b border-[#d0e8f0] rounded-b-2xl bg-white" data-testid="practitioners-list">
              {loading ? (
                <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#35AFE4]" /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <MapPin size={48} className="mx-auto text-gray-300 mb-3" />
                  <p className="font-bold text-gray-700 mb-1">Aucun praticien trouvé</p>
                  <p className="text-sm text-gray-400">Essayez de modifier vos filtres</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filtered.map((d, i) => (
                    <div key={d.id || i}
                      className={`p-4 transition-all cursor-pointer ${hoveredId === d.id ? 'bg-[#eaf6fc]' : 'hover:bg-gray-50'}`}
                      onMouseEnter={() => setHoveredId(d.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onClick={() => navigate(`/annuaire/${d.id}`)}
                      data-testid={`practitioner-${i}`}
                    >
                      <div className="flex items-start gap-3">
                        <img
                          src={d.profile_image ? (d.profile_image.startsWith('http') ? d.profile_image : `${process.env.REACT_APP_BACKEND_URL}${d.profile_image}`) : '/avatar-SFOALC.png'}
                          alt={d.name}
                          className="w-11 h-11 rounded-full object-cover flex-shrink-0 border-2 border-[#d0e8f0]"
                          onError={e => { e.target.src = '/avatar-SFOALC.png'; }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-bold text-[#1e3a5f] text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>
                              {d.name}
                            </h3>
                            {typeof d.distance_km === 'number' && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#35AFE4]/10 text-[#35AFE4] flex-shrink-0 flex items-center gap-1" data-testid={`distance-${i}`}>
                                <MapPin size={9} />
                                {d.distance_km < 1
                                  ? `${Math.round(d.distance_km * 1000)} m`
                                  : d.distance_km < 10
                                    ? `${d.distance_km.toFixed(1)} km`
                                    : `${Math.round(d.distance_km)} km`}
                              </span>
                            )}
                          </div>
                          {d.phone && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <Phone size={10} /> {d.phone}
                            </p>
                          )}
                          {d.address && (
                            <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                              <MapPin size={10} /> {d.address}
                            </p>
                          )}
                          {d.specialties && d.specialties.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {d.specialties.map((s, si) => (
                                <span key={si} className="text-[10px] px-2 py-0.5 bg-[#eaf6fc] text-[#35AFE4] rounded-full font-medium">{s}</span>
                              ))}
                            </div>
                          )}
                          {(d.address || d.city) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const dest = encodeURIComponent([d.address, d.postal_code, d.city, 'France'].filter(Boolean).join(', '));
                                const isApple = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
                                const url = isApple
                                  ? `https://maps.apple.com/?daddr=${dest}`
                                  : `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              }}
                              className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1e3a5f] text-white text-[10px] font-bold hover:bg-[#162d4a] transition-colors"
                              data-testid={`itineraire-${i}`}
                            >
                              <Navigation size={10} strokeWidth={2.5} /> Itinéraire
                            </button>
                          )}
                        </div>
                        <ChevronRight size={14} className="text-gray-300 flex-shrink-0 mt-1" />
                      </div>
                    </div>
                  ))}

                  {banners.map((b, i) => (
                    <a key={`banner-${i}`} href={b.link_url} target="_blank" rel="noopener noreferrer"
                      className="block p-3 hover:bg-gray-50 transition-all"
                      data-testid={`ad-banner-${i}`}
                      onClick={() => { if (b.id) axios.post(`${API}/ad-banners/${b.id}/click`).catch(() => {}); }}
                    >
                      <img src={b.image_url.startsWith('/') ? `${API.replace('/api', '')}${b.image_url}` : b.image_url} alt={b.title} className="w-full h-auto rounded-lg" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT (desktop only): Mapbox Map */}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DirectoryPage;
